import React, { useState, useEffect, useRef, useContext } from "react";
// Component Imports
import MenuScreen from "./MenuScreen";
import LoaderScreen from "./LoaderScreen";
import ErrorScreen from "./ErrorScreen";
import ModalScreen from "./ModalScreen";
import RowList from "../Module/TvRadio/TvRadioRowList";
import TvRadioDetailsScreen from "../Module/TvRadio/TvRadioDetailsScreen";
import TvRadioMarkupGrid from "../Module/TvRadio/TvRadioMarkupGrid";
import { Translate } from "./LanguageChanger";
// Asset Imports
import errorIcon from "../assets/NoDataFound.png";
import loginimg from "../assets/loginimg.png";
import solidbackgroundimg from "../assets/solidbackgroundimg.png";
import logoutIcon from "../assets/Logout-Icon.png";

// Service & Context Imports
import useNetworkStatus from "./NetworkStatusCheck";
import {
  getFavourites,
  getLiveChannelDetails,
  getLiveChannelsV2,
  getMasterHitV2,
  logoutUser,
} from "../services/services";
import { sendTestMatomoEvent } from "../Analytics/MatomoManager/matomoTest";
import { AppContext } from "../AppContext";
import { fetchWithCache } from "../globalManager/CacheManager"; // Ensure this import is correct

// --- CONSTANTS ---
const RESOLUTION = window.RESOLUTION;

// Enum for Screen Switching
const SCREEN_TYPES = {
  VIDEO: "VDO",
  TV_RADIO_DETAILS: "TvRadioDetailsScreen"
};

// Tab Definitions
const TAB_IDS = {
  TV: { id: 0, title: "TV", setRowId: 0 },
  RADIO: { id: 1, title: "Radio", setRowId: 1 },
  Favourite: { id: 2, title: "My Favorite", setRowId: 2 }
};

const INITIAL_TV_TAG = { id: 170, title: "All" };

// --- GLOBAL VARIABLES ---
let filterTags = [];
let homeTags = [];
let selectedTabId = -1;
let lastRememberFocusedRowList = "TV"; 

const LiveScreen = ({ navigate, isLogedIn }) => {
  // --- REFS ---
  const detailTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  // --- CONTEXT ---
  const { setSourceContext } = useContext(AppContext);
  const { isOnline } = useNetworkStatus();

  // Create translated tab data inside component
  const translatedTabs = [
    { id: 0, title: <Translate langKey="TV" />, setRowId: 0, key: "TV" },
    { id: 1, title: <Translate langKey="Radio" />, setRowId: 1, key: "Radio" }
  ];

  // --- STATE ---
  const [state, setState] = useState({
    isLogedInUser: !!isLogedIn,
    loading: true,
    isError: false,
    shouldShowModal: false,
    shouldShowAuthModal: false,
    shouldFocusToGrid: false,
    shouldFocusGrid: false,
    shouldFocusToRowList: false,
    shouldFocusToTab: false,
    shouldFocusLiveTvBTN: lastRememberFocusedRowList === "TV",
    liveRadioBtn: lastRememberFocusedRowList === "Radio",
    shouldCloseMenu: true,
    shouldShowCatMenu: true,
    shouldLoadData: true,
    EPGData: {},
    MasterChannelData: {},
    liveData: [],
    liveVDOurl: "",
    DRMToken: null,
    typ: "hls",
    errTitle: "",
    errDescription: "",
    errIcon: "",
    button: ["Logout", "Cancel"],
    iconWdth: "",
    iconHeight: "",
    screenAdditional: SCREEN_TYPES.EPG,
    rememberFocusItem: null,
    lastFocusedElementRowList: null,
  });

  const [screen, setScreen] = useState();
  const [data, setData] = useState({});
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedTags, setSelectedTags] = useState(INITIAL_TV_TAG);
  const [firstTag, setFirstTag] = useState("Tv");

  // --- STYLES (No useMemo) ---
  const styles = {
    liveContainer: {
      background: `url(${solidbackgroundimg})`,
      display: "flex",
      flexDirection: "column",
      width: "100vw",
      height: "100vh",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      overflow: "hidden",
    },
  };

  // --- LIFECYCLE ---
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (detailTimerRef.current) {
        clearTimeout(detailTimerRef.current);
        detailTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setSourceContext(`Tv/Tag(170/All)`);
    setSelectedTags(INITIAL_TV_TAG);
    setFirstTag("Tv");
    localStorage.setItem("pageName", "tv-radio");

    sendTestMatomoEvent({
      eventCategory: "Page",
      eventAction: "View",
      eventName: "Live",
      url: "tv-radio"
    });
  }, [setSourceContext]);

  useEffect(() => {
    if (!isMountedRef.current) return;
    setState(prev => ({
      ...prev,
      shouldFocusGrid: true,
      shouldFocusToRowList: false,
      shouldFocusToTab: false,
    }));
  }, [isOnline]);

  // --- FUNCTIONS (Standard JS, No useCallback) ---

  const updateState = (updates) => {
    if (isMountedRef.current) {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  const loadData = async (selectedTabId) => {
    if (!selectedTabId) return;
    updateState({ loading: true });

    try {
      const fetchedEPGData = await fetchWithCache(
        `LiveChannelsV2_TV_${selectedTabId}`,
        () => getLiveChannelsV2(selectedTabId, 0)
      );
      
      if (!isMountedRef.current) return;

      updateState({
        loading: false,
        EPGData: fetchedEPGData,
        shouldLoadData: false,
        isError: false,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      updateState({ loading: false });
    }
  };

  const loadDataRadio = async (selectedTabId) => {
    if (!selectedTabId) return;
    updateState({ loading: true });

    try {
      const fetchedEPGData = await fetchWithCache(
        `LiveChannels_Radio_${selectedTabId}`,
        () => getLiveChannelsV2(selectedTabId)
      );
      
      if (!isMountedRef.current) return;

      updateState({
        loading: false,
        EPGData: fetchedEPGData,
        shouldLoadData: false,
        isError: false,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      updateState({ loading: false });
    }
  };

  const fetchTags = async (type) => {
    updateState({ loading: true });
    try {
      const response = await fetchWithCache(
        "MasterHitV2",
        () => getMasterHitV2()
      );
      if (!isMountedRef.current) return;

      const tagsData = type === "TV" ? response.data.channelTags : response.data.radioTags;
      
      filterTags = [];
      homeTags = [];
      let rowCounter = 0;
      const appLanguage = localStorage.getItem("AppLanguage") || "English";

      tagsData.forEach((tag) => {
        const titleObj = tag.title && tag.title.find(item => item.language === appLanguage);
        const titleContent = titleObj ? titleObj.content : "Unknown";

        const mdl = {
          id: tag.id,
          title: titleContent,
          setRowId: rowCounter++,
        };
        
        filterTags.push(mdl);
        homeTags.push(mdl.id);
      });

      if (filterTags.length > 0) {
        selectedTabId = filterTags[0].id;
      }

      updateState({
        loading: false,
        bannerdata: response.data.banners,
        filterTags,
      });

      const loadFunction = type === "TV" ? loadData : loadDataRadio;
      if (filterTags.length > 0) {
        loadFunction(filterTags[0].id);
      }
    } catch (error) {
      console.error("Error fetching tags:", error);
      updateState({ loading: false });
    }
  };

  const MaterData = () => {
    setSourceContext(`Tv/Tag(170/All)`);
    fetchTags("TV");
  };

  const RadioTags = () => {
    setSourceContext(`Radio/Tag(172/All)`);
    fetchTags("Radio");
  };

  // Initial Load
  useEffect(() => {
    if (state.shouldLoadData) {
      lastRememberFocusedRowList = "TV";
      setState(prev => ({
        ...prev,
        shouldFocusLiveTvBTN: true,
        liveRadioBtn: false,
      }));
      MaterData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMyFavouriteData = async () => {
    const userId = localStorage.getItem("userId");
    const headers = {
      platform: "0",
      iskid: "0",
      Langid: "1",
      timestamp: "0000000000",
      Authorization: `Bearer ${localStorage.getItem("JWTToken")}`,
      "Content-Type": "application/json",
    };

    try {
      const fetchedEPGData = await getFavourites(userId, headers);
      
      if (!isMountedRef.current) return;

      updateState({
        loading: false,
        EPGData: fetchedEPGData,
        shouldLoadData: false,
      });
    } catch (error) {
      console.error("Error fetching favourites:", error);
      if (!isMountedRef.current) return;

      updateState({
        EPGData: [],
        loading: false,
        shouldFocusToRowList: false,
        shouldLoadData: false,
        isError: true,
        shouldShowAuthModal: true,
        // errTitle: "Session-Expired",
        errDescription: "session-desc",
        errIcon: logoutIcon,
        iconWdth: "109px",
        iconHeight: "109px",
        button: ["Login"],
      });
    }
  };

  const navigateToScreen = (screenName, screenData = {}) => {
    setScreen(screenName);
    setData(screenData);
  };

  const handleMenuselection = (selectedMenu) => {
    const menuActions = {
      Login: () => navigate("LoginScreen"),
      Profile: () => navigate("ProfileScreen"),
      Search: () => navigate("SearchScreen"),
      Live: () => navigate("LiveScreen"),
      LiveEvents: () => navigate("LiveEventsScreen"),
      Home: () => navigate("HomePage"),
      Categories: () => navigate("CategoryScreen"),
      Settings: () => navigate("SettingsScreen"),
    };
    if (menuActions[selectedMenu]) menuActions[selectedMenu]();
  };

  const handleBackButton = () => {
    updateState({
      shouldFocusToRowList: false,
      shouldCloseMenu: false,
      shouldFocusToTab: false,
    });
    MenuScreen.SetFocus(false);
  };

  const handleRightButton = () => {
    updateState({ shouldFocusToTab: true, shouldCloseMenu: true });
    MenuScreen.SetFocus(true);
  };

  const handleLeftButton = () => {
    updateState({
      shouldFocusToRowList: false,
      shouldFocusToTab: false,
      shouldFocusToGrid: false,
      shouldCloseMenu: false,
    });
    MenuScreen.SetFocus(false);
  };

  const showModal = (config) => {
    updateState({ shouldCloseMenu: true, shouldShowModal: true, ...config });
    MenuScreen.SetFocus(true);
  };

  const handleLogout = () => {
    showModal({
      errTitle: "Logout",
      errDescription: "cofirm-logout",
      errIcon: logoutIcon,
      iconWdth: "109px", iconHeight: "109px", button: ["Logout", "Cancel"],
    });
  };

  const handleLogin = () => {
    showModal({
      errTitle: "Login to Continue",
      errDescription: "LoginContinue",
      errIcon: loginimg,
      iconWdth: "109px", iconHeight: "109px", button: ["Login", "Cancel"],
    });
  };

  const handleExit = () => {
    showModal({
      errTitle: "Exit",
      errDescription: "ExitMsg",
      errIcon: logoutIcon,
      iconWdth: "109px", iconHeight: "109px", button: ["Yes", "No"],
    });
  };

  const modalOKButtonHandler = (action) => {
    switch (action) {
      case "Login":
        localStorage.removeItem("user_details");
        localStorage.removeItem("userId");
        localStorage.removeItem("profileId");
        navigate("LoginScreen");
        break;
      case "Logout":
        logoutUser();
        localStorage.clear();
        if (window.appReload) window.appReload();
        if (MenuScreen.ReseTfocus) MenuScreen.ReseTfocus(4);
        MenuScreen.SetFocus(true);
        handleMenuselection("Login");
        break;
      case "Exit":
        try {
            if (window.tizen) {
                window.tizen.application.getCurrentApplication().exit();
            } else if (window.webOS) {
                window.webOS.platformBack();
            } else {
                window.close();
                window.history.back();
            }
        } catch (e) {
            console.warn("Exit failed, forcing close", e);
            window.close();
        }
        break;
      default: break;
    }
  };

  const modalCANCELButtonHandler = () => {
    if (MenuScreen.ReseTfocus) MenuScreen.ReseTfocus(3);
    updateState({ shouldShowModal: false, shouldCloseMenu: false });
    MenuScreen.SetFocus(false);
    sendTestMatomoEvent({
      eventCategory: "Page", eventAction: "View", eventName: "CancelPopup", url: "CancelPopup"
    });
  };

  const handleBack = (screenName) => {
    const tagName = selectedTags?.title || "All";
    const tagId = selectedTags?.id || "170";
    setSourceContext(`${firstTag}/Tag(${tagId}/${tagName})`);
    
    updateState({
      loading: false,
      shouldFocusToKeyBoard: false,
      shouldFocusGrid: true,
      shouldCloseMenu: true,
      shouldFocusToTab: false,
      shouldShowModal: false,
    });
    setScreen(screenName);
  };

  const RowlListcallBack = (selectedItem) => {
    setSourceContext(`${firstTag}/Tag(${selectedItem.id}/${selectedItem.key || selectedItem.title})`);
    
    // Use the key property for tab identification
    const itemKey = selectedItem.key || selectedItem.title;
    
    if (itemKey !== "Radio" && itemKey !== "TV" && itemKey !== "My Favorite") {
      setSelectedTags(selectedItem);
    }

    if (itemKey === "Radio") {
        setFirstTag("Radio");
        updateState({
          shouldShowCatMenu: true,
          shouldFocusToRowList: false,
          shouldFocusLiveTvBTN: false,
          liveRadioBtn: true,
          lastFocusedElementRowList: true,
          rememberFocusItem: 0,
          loading: true,
          EPGData: {}
        });
        lastRememberFocusedRowList = "Radio";
        RadioTags();
    } else if (itemKey === "TV") {
        setFirstTag("TV");
        updateState({
          shouldShowCatMenu: true,
          shouldFocusToRowList: false,
          shouldFocusLiveTvBTN: true,
          liveRadioBtn: false,
          lastFocusedElementRowList: true,
          rememberFocusItem: 0,
          loading: true,
          EPGData: {}
        });
        lastRememberFocusedRowList = "TV";
        MaterData();
    } else if (itemKey === "My Favorite") {
        setFirstTag("My Favorite");
        updateState({
          shouldShowCatMenu: false,
          shouldFocusToRowList: false,
          shouldFocusLiveTvBTN: false,
          liveRadioBtn: false,
          lastFocusedElementRowList: true,
          rememberFocusItem: 0,
          shouldLoadData: false,
          loading: true,
          EPGData: {}
        });
        lastRememberFocusedRowList = "Favourite";
        loadMyFavouriteData();
    } else if (itemKey === "Live Event") {
        updateState({ shouldFocusToTab: false });
    } else {
        const commonState = {
            shouldCloseMenu: false,
            shouldFocusToRowList: true,
            shouldFocusToGrid: false,
            rememberFocusItem: selectedItem.setRowId,
        };

        if (state.shouldFocusLiveTvBTN) {
            updateState({
              ...commonState,
              shouldFocusLiveTvBTN: true,
              lastFocusedElementRowList: true,
            });
            loadData(selectedItem.id);
        } else if (state.liveRadioBtn) {
            updateState({
              ...commonState,
              liveRadioBtn: true,
              lastFocusedElementRowList: true,
            });
            loadDataRadio(selectedItem.id);
        }
    }
  };

  const callBack = (selectedItem) => {
    updateState({ shouldFocusGrid: false, shouldCloseMenu: false });
    MenuScreen.SetFocus(false);
  };

  const handleItemSelected = async (item) => {
    const headers = {
      platform: "0",
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("JWTToken")}`,
    };

    try {
      const fetchedEPGData = await fetchWithCache(
        `LiveChannelDetails${item.id}`,
        () => getLiveChannelDetails(item.id, headers)
      );
      
      if (!isMountedRef.current) return;

      const eventName = state.liveRadioBtn ? "LiveRadio" : "LiveChannel";
      sendTestMatomoEvent({
        eventCategory: "Live", eventAction: "Select", eventName,
      });

      navigateToScreen(SCREEN_TYPES.TV_RADIO_DETAILS, fetchedEPGData && fetchedEPGData.data ? fetchedEPGData.data : {});
    } catch (error) {
      console.error("Error fetching channel details:", error);
    }
  };

  const handleRowListUpButton = () => {
    updateState({
      shouldFocusToRowList: false, shouldFocusToTab: true, shouldFocusGrid: false,
    });
  };

  const handleTabUpButton = () => {};

  const handleRowListDownButton = () => {
    if (state.shouldFocusToTab) {
      updateState(state.shouldShowCatMenu ? {
        shouldFocusToRowList: true,
        shouldFocusToTab: false,
       shouldFocusGrid: false,
      } : {
        shouldFocusGrid: true,
        shouldFocusToTab: false,
        shouldFocusToRowList: false,
      });
    } else if (state.shouldFocusToRowList) {
      updateState({
        shouldFocusToTab: false,
        shouldFocusToRowList: false,
        shouldFocusGrid: true,
      });
    }
  };

  const handleUPButton = () => {
    updateState(state.shouldShowCatMenu ? {
      shouldFocusToRowList: true,
      shouldFocusGrid: false,
      shouldFocusToTab: false,
    } : {
      shouldFocusGrid: false,
      shouldFocusToRowList: false,
      shouldFocusToTab: true,
    });
  };

  const detailToDetail = (recommendationItem) => {
    if (detailTimerRef.current) {
      clearTimeout(detailTimerRef.current);
      detailTimerRef.current = null;
    }

    updateState({ loading: true });
    navigateToScreen("", recommendationItem);

    detailTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      updateState({ loading: false });
      navigateToScreen(SCREEN_TYPES.TV_RADIO_DETAILS, recommendationItem);
    }, 100);
  };

  const FocusHandler = () => {};

  // --- RENDER ---
  if (screen === SCREEN_TYPES.TV_RADIO_DETAILS) {
    return (
      <TvRadioDetailsScreen
        handleBack={handleBack}
        itemDetails={data}
        firstTag={firstTag}
        data={data}
        isLogedIn={isLogedIn}
        isLiveScreen={true}
        detailToDetail={detailToDetail}
      />
    );
  }

  const isFHD = RESOLUTION === "FHD" || window.innerWidth >= 1920;

  return (
    <div style={styles.liveContainer}>
      {state.loading && <LoaderScreen />}

      {/* Categories Row */}
      {state.MasterChannelData && state.shouldShowCatMenu && (
        <div
          style={{
            position: "absolute", marginTop: "7rem", width: "90%", left: "5%",
            height: "75px", overflow: "hidden", zIndex: 1,
          }}
          onMouseEnter={() => updateState({
            shouldFocusToRowList: true, shouldFocusToTab: false, shouldFocusGrid: false,
          })}
        >
          <RowList
            rowHeight="70px" columnWidth="50%" columnHeight="70px" itemWidth="280px" itemHeight="70px"
            animationSpeed={300} bgColor="transparent" borderColor={state.shouldFocusToRowList ? "#EC008C" : "#EC008C"}
            border="2px solid #fff" borderRadius="10px" fontSize="26px" fontColor="#FFFFFF"
            selectionStyle="background" gapBetweenColumn={5}
            shouldFocus={state.shouldFocusToRowList}
            FocusHandler={FocusHandler}
            handleLeftButton={handleLeftButton}
            RowlListcallBack={RowlListcallBack}
            handleBackButton={handleBackButton}
            handleRowListUpButton={handleRowListUpButton}
            handleRowListDownButton={handleRowListDownButton}
            data={filterTags}
            activeStyle="underline"
            lastFocusedElementRowList={state.lastFocusedElementRowList}
            rememberFocusItem={state.rememberFocusItem}
            screen="LiveScreen"
          />
        </div>
      )}

      {/* Tabs Row */}
      <div
        style={{
          position: "absolute", left: "5%", marginTop: "15px", zIndex: 1,
        }}
        onMouseEnter={() => updateState({
          shouldFocusToTab: true, shouldFocusToRowList: false, shouldFocusGrid: false,
        })}
      >
        <RowList
          rowHeight="70px" columnWidth="50%" columnHeight="70px" itemWidth="280px" itemHeight="70px"
          animationSpeed={300} bgColor="transparent" borderColor={state.shouldFocusToTab ? "#EC008C" : "#EC008C"}
          border="2px solid #fff" borderRadius="10px" fontSize="26px" fontColor="#FFFFFF"
          selectionStyle="background" gapBetweenColumn={10}
          shouldFocus={state.shouldFocusToTab}
          FocusHandler={FocusHandler}
          handleLeftButton={handleLeftButton}
          RowlListcallBack={RowlListcallBack}
          handleBackButton={handleBackButton}
          handleRowListUpButton={handleTabUpButton}
          handleRowListDownButton={handleRowListDownButton}
          screen="LiveScreen"
          data={translatedTabs}
          activeStyle="underline"
          lastRememberFocusedRowList={lastRememberFocusedRowList}
        />
      </div>

      <div
        style={{
          width: "90%", height: "2px", background: "#FFFFFF",
          position: "absolute", left: "5%", marginTop: "100px", opacity: "0.5",
        }}
      />

      {/* Grid Area */}
      <div
        style={{ position: "relative" }}
        onMouseEnter={() => updateState({
          shouldFocusGrid: true, shouldFocusToRowList: false, shouldFocusToTab: false,
        })}
      >
        {state.EPGData.data && (
          <TvRadioMarkupGrid
            items={state.EPGData.data}
            onItemSelected={handleItemSelected}
            rowWidth="95%" rowHeight={280}
            columnWidth={isFHD ? 394 : 152} columnHeight={isFHD ? 221 : 194}
            itemWidth={isFHD ? 404 : 152} itemHeight={isFHD ? 232 : 194}
            numberOfColumnsEachRow={4} numberOfDefaultRows={3}
            selectionColor={state.shouldFocusGrid ? "#ffffff" : "transparent"} selectionStyle="border"
            gapBetweenColumns={35} gapBetweenRows={isFHD ? 10 : 7}
            columnBgColorOrImage="#000000" rowBgColorOrImage="transparent"
            shouldScrollHorizontally={false} shouldScrollVertically={true} scrollDuration={300}
            GridXCordinate={0} GridYCordinate={0}
            shouldFocus={state.shouldFocusGrid}
            callBack={callBack}
            itemComponentName="LiveChannelWidget"
            handleUPButton={handleUPButton}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
          />
        )}

    
          <MenuScreen
            navigate={navigateToScreen}
            handleMenuselection={handleMenuselection}
            shouldCloseMenu={state.shouldCloseMenu}
            handleRightButton={handleRightButton}
            isLogedIn={isLogedIn}
            appNavigate={navigate}
            handleLogin={handleLogin}
            handleLogout={handleLogout}
            handleExit={handleExit}
          />
     

        <div style={{ position: "absolute", top: "0" }}>
          <ErrorScreen errorIcon={errorIcon} disply={state.isError ? "flex" : "none"} />
        </div>

        <ModalScreen
          shouldShowError={state.shouldShowModal}
          errTitle={state.errTitle}
          errDescription={state.errDescription}
          errIcon={state.errIcon}
          button={state.button}
          iconWdth={state.iconWdth}
          iconHeight={state.iconHeight}
          modalOKButtonHandler={modalOKButtonHandler}
          modalCANCELButtonHandler={modalCANCELButtonHandler}
        />
      </div>
    </div>
  );
};

export default LiveScreen;